import { useCallback, useEffect, useRef, useState } from 'react'

import { mockEditImage } from 'Pods/ThreeDStudio/Components/ImageAssistantPanel/mockData'
import { MAX_ATTACHMENTS } from 'Pods/ThreeDStudio/constants'
import useSourceUrlImageUpload from 'Pods/ThreeDStudio/Hooks/useSourceUrlImageUpload'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import { setInputImage } from 'Pods/ThreeDStudio/Store/actions/modelSettings'
import { closeImageAssistant } from 'Pods/ThreeDStudio/Store/actions/ui'

import { EMPTY_ARRAY } from 'Utils/stableDefaults'

import useMaskCanvas from './useMaskCanvas'

/**
 * Owns the Edit canvas state: the image loaded onto the mask canvas, the composer
 * (prompt + reference attachments), the (mocked) generated-version history, and
 * the History dropdown. Composes useMaskCanvas for the drawing tools.
 */
export default function useEditCanvas() {
  const editImage = useThreeDStudioStore(state => state.ui.editImage)
  const canvas = useMaskCanvas()

  const [currentImage, setCurrentImage] = useState(editImage)
  const [versions, setVersions] = useState(EMPTY_ARRAY)
  const [prompt, setPrompt] = useState('')
  const [attachments, setAttachments] = useState(EMPTY_ARRAY)
  const [isHistoryOpen, setIsHistoryOpen] = useState(false)
  const [isSending, setIsSending] = useState(false)

  const fileInputRef = useRef(null)
  const idRef = useRef(0)
  const versionCountRef = useRef(0)
  const isSendingRef = useRef(false)
  const isMountedRef = useRef(true)

  useEffect(() => () => { isMountedRef.current = false }, [])

  const uploadFromUrl = useSourceUrlImageUpload(setInputImage)

  const nextId = () => {
    idRef.current += 1
    return idRef.current
  }

  const handlePromptChange = useCallback(event => setPrompt(event.target.value), [])

  const handleUploadClick = useCallback(() => fileInputRef.current?.click(), [])

  const handleRemoveAttachment = useCallback((id) => {
    setAttachments((prev) => {
      const removed = prev.find(image => image.id === id)
      if (removed) {
        URL.revokeObjectURL(removed.url)
      }
      return prev.filter(image => image.id !== id)
    })
  }, [])

  const handleFilesChange = useCallback((event) => {
    const picked = Array.from(event.target.files || [])
    const remaining = MAX_ATTACHMENTS - attachments.length
    if (picked.length && remaining > 0) {
      const added = picked
        .slice(0, remaining)
        .map(file => ({ id: nextId(), url: URL.createObjectURL(file) }))
      setAttachments(prev => [...prev, ...added])
    }
    // Reset so picking the same file again still fires change.
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [attachments])

  // Send requires a painted mask; the prompt and reference images are optional.
  const handleSend = useCallback(() => {
    if (!canvas.hasPainted || isSendingRef.current) {
      return
    }
    isSendingRef.current = true
    setIsSending(true)

    mockEditImage().then((image) => {
      if (!isMountedRef.current) {
        return
      }
      versionCountRef.current += 1
      const version = { id: image.id, url: image.url, label: `Version ${versionCountRef.current}` }
      // Newest first so the History dropdown lists the latest version on top.
      setVersions(prev => [version, ...prev])
      setCurrentImage(version)
      setPrompt('')
      setAttachments((prev) => {
        prev.forEach(item => URL.revokeObjectURL(item.url))
        return EMPTY_ARRAY
      })
      canvas.clear()
      isSendingRef.current = false
      setIsSending(false)
    })
  }, [canvas])

  const handleToggleHistory = useCallback(() => setIsHistoryOpen(prev => !prev), [])

  const handleCloseHistory = useCallback(() => setIsHistoryOpen(false), [])

  const handleSelectVersion = useCallback((version) => {
    setCurrentImage(version)
    setIsHistoryOpen(false)
    canvas.clear()
  }, [canvas])

  const handleApply = useCallback(async () => {
    if (!currentImage) {
      return
    }
    const absoluteUrl = new URL(currentImage.url, window.location.origin).href
    const uploaded = await uploadFromUrl(absoluteUrl)
    if (uploaded) {
      closeImageAssistant()
    }
  }, [currentImage, uploadFromUrl])

  // Keep the latest attachments in a ref so the unmount cleanup revokes the
  // current object URLs without re-running (and revoking still-rendered URLs) on
  // every attachment change. Removal and send already revoke their own URLs.
  const attachmentsRef = useRef(attachments)
  attachmentsRef.current = attachments

  useEffect(() => () => {
    attachmentsRef.current.forEach(item => URL.revokeObjectURL(item.url))
  }, [])

  return {
    canvas,
    editImage,
    currentImage,
    versions,
    prompt,
    attachments,
    isHistoryOpen,
    isSending,
    isSendDisabled: !canvas.hasPainted || isSending,
    isUploadDisabled: attachments.length >= MAX_ATTACHMENTS,
    fileInputRef,
    handlePromptChange,
    handleUploadClick,
    handleRemoveAttachment,
    handleFilesChange,
    handleSend,
    handleToggleHistory,
    handleCloseHistory,
    handleSelectVersion,
    handleApply,
  }
}
